/**
 * f3-cart-drawer.js
 * F3 Gear custom sliding cart drawer.
 * Supersedes Impulse's native drawer via capture-phase click + submit interception.
 * Also supersedes Snap Cart Drawer (disable embed at publish; QA on TeeInBlue PDPs).
 * Seal Subscriptions: subscription line items render via selling_plan_allocation in item snippet.
 * Ported from GripSocks gs-cart-drawer.js (2026-06-09)
 */
(function () {
  'use strict';

  const SECTION_ID = 'f3-cart-drawer';
  const POPUP_SECTION_ID = 'f3-checkout-popup';
  const CONTENTS_ID = 'F3CartDrawerContents';
  const DRAWER_ID = 'F3CartDrawer';
  const OVERLAY_ID = 'F3CartOverlay';
  const ANNOUNCE_ID = 'F3CartAnnouncement';

  /* ------------------------------------------------------------------ */
  /* DOM refs (resolved lazily once drawer is in DOM)                     */
  /* ------------------------------------------------------------------ */
  function drawer() { return document.getElementById(DRAWER_ID); }
  function contents() { return document.getElementById(CONTENTS_ID); }
  function overlay() { return document.getElementById(OVERLAY_ID); }
  function announce() { return document.getElementById(ANNOUNCE_ID); }

  /* ------------------------------------------------------------------ */
  /* Focus trap (accessibility — keyboard users can't tab out of drawer) */
  /* ------------------------------------------------------------------ */
  var _trapHandler = null;
  var _lastCartFocus = null;

  function trapFocus(el) {
    releaseFocus();
    requestAnimationFrame(function () {
      var focusable = getFocusable(el);
      if (focusable.length) focusable[0].focus();
      _trapHandler = function (e) {
        if (e.key !== 'Tab') return;
        var f = getFocusable(el);
        if (!f.length) return;
        if (e.shiftKey) {
          if (document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        } else {
          if (document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
        }
      };
      document.addEventListener('keydown', _trapHandler);
    });
  }

  function releaseFocus() {
    if (_trapHandler) { document.removeEventListener('keydown', _trapHandler); _trapHandler = null; }
  }

  function getFocusable(el) {
    return Array.from(el.querySelectorAll(
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])'
    )).filter(function (n) { return n.offsetParent !== null; });
  }

  /* ------------------------------------------------------------------ */
  /* Open / close                                                          */
  /* ------------------------------------------------------------------ */
  function openDrawer() {
    const d = drawer();
    if (!d) return;
    _lastCartFocus = document.activeElement;
    d.setAttribute('aria-hidden', 'false');
    d.classList.add('is-open');
    const ov = overlay();
    if (ov) { ov.classList.add('is-visible'); ov.setAttribute('aria-hidden', 'false'); }
    document.body.classList.add('f3-drawer-open');
    trapFocus(d);
  }

  function closeDrawer() {
    const d = drawer();
    if (!d) return;
    releaseFocus();
    d.setAttribute('aria-hidden', 'true');
    d.classList.remove('is-open');
    const ov = overlay();
    if (ov) { ov.classList.remove('is-visible'); ov.setAttribute('aria-hidden', 'true'); }
    document.body.classList.remove('f3-drawer-open');
    if (_lastCartFocus && _lastCartFocus.focus) {
      _lastCartFocus.focus();
    } else {
      const cartIcon = document.querySelector('.js-drawer-open-cart');
      if (cartIcon) cartIcon.focus();
    }
    _lastCartFocus = null;
  }

  /* ------------------------------------------------------------------ */
  /* Section Rendering API helpers                                         */
  /* ------------------------------------------------------------------ */
  function buildSectionsParam() {
    let param = SECTION_ID;
    if (document.getElementById('F3CheckoutPopup') !== null ||
        document.querySelector('[data-section-id="' + POPUP_SECTION_ID + '"]') !== null) {
      param += ',' + POPUP_SECTION_ID;
    }
    return param;
  }

  function extractSectionHtml(responseJson, sectionId) {
    // /cart/add.js and /cart/change.js wrap rendered sections in a `sections`
    // object; the Section Rendering API (`/?sections=...`) returns them at the
    // TOP LEVEL of the JSON. Handle both — refreshCart() uses the latter, and
    // looking only under `sections` made every refresh() a silent no-op.
    if (responseJson.sections && responseJson.sections[sectionId]) {
      return responseJson.sections[sectionId];
    }
    if (typeof responseJson[sectionId] === 'string') {
      return responseJson[sectionId];
    }
    return null;
  }

  function syncPopupCartState(responseJson) {
    const popupHtml = extractSectionHtml(responseJson, POPUP_SECTION_ID);
    if (popupHtml && window.F3CheckoutPopup && typeof window.F3CheckoutPopup.refreshCartState === 'function') {
      window.F3CheckoutPopup.refreshCartState(popupHtml);
    }
  }

  function updateContents(sectionHtml) {
    if (!sectionHtml) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = sectionHtml;
    const newContents = tmp.querySelector('#' + CONTENTS_ID);
    const current = contents();
    if (newContents && current) {
      current.replaceWith(newContents);
      bindContentEvents();
      updateCartCountBubbles(newContents);
      checkAndFireConfetti(newContents);
      handleRewardsAutoState();

      // Announce the cart change so other widgets (e.g. the bundle builder
      // box tracker) can re-sync from /cart.js. updateContents runs after
      // every drawer-side cart mutation, so this single hook covers qty
      // changes, removals, upsell adds, and rewards add/remove. Listeners
      // debounce, so render-only refreshes cost at most one extra fetch.
      document.dispatchEvent(new CustomEvent('f3:cart:changed'));
    }
  }

  function refreshCart() {
    const sectionsParam = buildSectionsParam();
    return fetch('/?sections=' + sectionsParam)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        const sectionHtml = extractSectionHtml(data, SECTION_ID);
        updateContents(sectionHtml);
        syncPopupCartState(data);
      })
      .catch(function (err) { console.error('[F3CartDrawer] refreshCart error:', err); });
  }

  /* ------------------------------------------------------------------ */
  /* Cart count bubbles (header icon badge)                               */
  /* ------------------------------------------------------------------ */
  function updateCartCountBubbles(contentsEl) {
    const el = contentsEl || contents();
    if (!el) return;
    const countEl = el.querySelector('#F3CartCount') || document.getElementById('F3CartCount');
    const itemCount = el.dataset ? parseInt(el.dataset.itemCount || '0', 10) : 0;
    document.querySelectorAll('[data-cart-count], .js-cart-count').forEach(function (badge) {
      badge.textContent = itemCount;
    });
    if (countEl) countEl.textContent = itemCount;
  }

  /* ------------------------------------------------------------------ */
  /* Capture-phase drawer-open interception                               */
  /* Blocks Impulse's native drawer from opening on cart-icon clicks.     */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    if (e.target.closest('.js-drawer-open-cart, [data-f3-cart-open]')) {
      e.preventDefault();
      e.stopPropagation();
      openDrawer();
      return;
    }
    if (e.target.closest('.f3-cart-drawer__close')) {
      closeDrawer();
      return;
    }
    if (e.target.id === OVERLAY_ID) {
      closeDrawer();
      return;
    }
  }, true); // capture phase

  /* ------------------------------------------------------------------ */
  /* Prevent native ajaxProduct:added from opening Impulse's drawer       */
  /* ------------------------------------------------------------------ */
  document.addEventListener('ajaxProduct:added', function (e) {
    e.stopImmediatePropagation();
  }, true);

  /* ------------------------------------------------------------------ */
  /* Capture-phase form submit interception (add-to-cart)                 */
  /* ------------------------------------------------------------------ */
  document.addEventListener('submit', handleFormSubmit, true);

  function handleFormSubmit(e) {
    const form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    const action = (form.getAttribute('action') || '').split('?')[0];
    if (!action.endsWith('/cart/add')) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    const formData = new FormData(form);
    const sectionsParam = buildSectionsParam();

    const params = new URLSearchParams();
    for (const [key, val] of formData.entries()) {
      params.append(key, val);
    }
    params.append('sections', sectionsParam);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString()
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        const sectionHtml = extractSectionHtml(data, SECTION_ID);
        if (sectionHtml) {
          updateContents(sectionHtml);
        } else {
          return refreshCart();
        }
        syncPopupCartState(data);
        openDrawer();
        announceMsg('Item added to cart.');
      })
      .catch(function (err) {
        console.error('[F3CartDrawer] add-to-cart error:', err);
      });
  }

  /* ------------------------------------------------------------------ */
  /* Delegated events inside #F3CartDrawer                                */
  /* ------------------------------------------------------------------ */
  function bindContentEvents() {
    // All events are delegated on the static drawer root — no rebinding needed
  }

  document.addEventListener('click', function (e) {
    const d = drawer();
    if (!d) return;

    // Remove line item
    const removeBtn = e.target.closest('[data-action="remove-item"]');
    if (removeBtn && d.contains(removeBtn)) {
      e.preventDefault();
      const key = removeBtn.dataset.key;
      if (key) changeCartItem(key, 0);
      return;
    }

    // Quantity increment / decrement
    const qtyBtn = e.target.closest('[data-action="increment"], [data-action="decrement"]');
    if (qtyBtn && d.contains(qtyBtn)) {
      e.preventDefault();
      const key = qtyBtn.dataset.key;
      const newQty = parseInt(qtyBtn.dataset.qty, 10);
      if (key && !isNaN(newQty) && newQty >= 0) changeCartItem(key, newQty);
      return;
    }

    // Upsell add
    const upsellBtn = e.target.closest('[data-action="upsell-add"]');
    if (upsellBtn && d.contains(upsellBtn)) {
      e.preventDefault();
      const variantId = upsellBtn.dataset.variantId
        || upsellBtn.closest('.f3-upsell-card').querySelector('[data-f3-upsell-variant-select]')?.value
        || upsellBtn.closest('.f3-upsell-card').querySelector('.f3-upsell-card__variant-btn--selected')?.dataset.variantId;
      if (variantId) addToCart(parseInt(variantId, 10), 1);
      return;
    }

    // Upsell variant button selection
    const variantBtn = e.target.closest('.f3-upsell-card__variant-btn');
    if (variantBtn && d.contains(variantBtn)) {
      const card = variantBtn.closest('.f3-upsell-card');
      card.querySelectorAll('.f3-upsell-card__variant-btn').forEach(function (b) {
        b.classList.remove('f3-upsell-card__variant-btn--selected');
      });
      variantBtn.classList.add('f3-upsell-card__variant-btn--selected');
      const addBtn = card.querySelector('[data-action="upsell-add"]');
      if (addBtn) addBtn.dataset.variantId = variantBtn.dataset.variantId;
      return;
    }

    // Rewards toggle (checkbox or slider)
    const rewardsToggle = e.target.closest('#F3RewardsToggle');
    if (rewardsToggle && d.contains(rewardsToggle)) {
      handleRewardsToggle(rewardsToggle.checked);
      return;
    }

    // Rewards add button (UI style: none, opted-out)
    const rewardsAddBtn = e.target.closest('[data-action="rewards-add"]');
    if (rewardsAddBtn && d.contains(rewardsAddBtn)) {
      e.preventDefault();
      handleRewardsToggle(true);
      return;
    }

    // Rewards remove link (UI style: none, opted-in)
    const rewardsWithoutBtn = e.target.closest('[data-action="rewards-without"]');
    if (rewardsWithoutBtn && d.contains(rewardsWithoutBtn)) {
      e.preventDefault();
      handleRewardsToggle(false);
      return;
    }
  });

  /* upsell variant dropdown change */
  document.addEventListener('change', function (e) {
    if (!e.target.closest || !e.target.hasAttribute('data-f3-upsell-variant-select')) return;
    const d = drawer();
    if (!d || !d.contains(e.target)) return;
    const card = e.target.closest('.f3-upsell-card');
    const addBtn = card && card.querySelector('[data-action="upsell-add"]');
    if (addBtn) addBtn.dataset.variantId = e.target.value;
  });

  /* ------------------------------------------------------------------ */
  /* Cart mutations                                                        */
  /* ------------------------------------------------------------------ */
  function changeCartItem(key, qty) {
    const sectionsParam = buildSectionsParam();
    setLoading(true);
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty, sections: sectionsParam })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        const sectionHtml = extractSectionHtml(data, SECTION_ID);
        updateContents(sectionHtml);
        syncPopupCartState(data);
      })
      .catch(function (err) { console.error('[F3CartDrawer] changeCartItem error:', err); })
      .finally(function () { setLoading(false); });
  }

  function addToCart(variantId, qty) {
    const sectionsParam = buildSectionsParam();
    setLoading(true);
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty, sections: sectionsParam })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        const sectionHtml = extractSectionHtml(data, SECTION_ID);
        if (sectionHtml) {
          updateContents(sectionHtml);
        } else {
          return refreshCart();
        }
        syncPopupCartState(data);
      })
      .catch(function (err) { console.error('[F3CartDrawer] addToCart error:', err); })
      .finally(function () { setLoading(false); });
  }

  /* ------------------------------------------------------------------ */
  /* Loading state                                                         */
  /* ------------------------------------------------------------------ */
  function setLoading(on) {
    const d = drawer();
    if (d) d.classList.toggle('is-loading', on);
  }

  /* ------------------------------------------------------------------ */
  /* Live-region announcement                                             */
  /* ------------------------------------------------------------------ */
  function announceMsg(msg) {
    const el = document.getElementById(ANNOUNCE_ID);
    if (el) { el.textContent = ''; requestAnimationFrame(function () { el.textContent = msg; }); }
  }

  /* ------------------------------------------------------------------ */
  /* Rewards auto-state on drawer open                                    */
  /* ------------------------------------------------------------------ */
  function handleRewardsAutoState() {
    const d = drawer();
    if (!d) return;
    const enabled = d.dataset.rewardsEnabled === 'true';
    if (!enabled) return;
    const defaultState = d.dataset.rewardsDefault;
    const contentsEl = contents();
    if (!contentsEl) return;
    const inCart = contentsEl.dataset.rewardsInCart === 'true';
    const variantId = parseInt(d.dataset.rewardsVariant, 10);
    if (!variantId) return;

    if (defaultState === 'opted-in' && !inCart) {
      const alreadyDismissed = sessionStorage.getItem('f3_rewards_default_applied');
      if (!alreadyDismissed) {
        addRewardsToCart(variantId);
        sessionStorage.setItem('f3_rewards_default_applied', '1');
      }
    }
  }

  function handleRewardsToggle(shouldAdd) {
    const d = drawer();
    if (!d) return;
    const variantId = parseInt(d.dataset.rewardsVariant, 10);
    const contentsEl = contents();
    if (!variantId || !contentsEl) return;
    const lineKey = contentsEl.dataset.rewardsLineKey || '';

    if (shouldAdd) {
      addRewardsToCart(variantId);
    } else {
      if (lineKey) {
        changeCartItem(lineKey, 0);
      }
    }
  }

  function addRewardsToCart(variantId) {
    const sectionsParam = buildSectionsParam();
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, sections: sectionsParam })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        const sectionHtml = extractSectionHtml(data, SECTION_ID);
        if (sectionHtml) updateContents(sectionHtml);
        syncPopupCartState(data);
      })
      .catch(function (err) { console.error('[F3CartDrawer] addRewards error:', err); });
  }

  /* ------------------------------------------------------------------ */
  /* Confetti                                                             */
  /* ------------------------------------------------------------------ */
  let confettiLib = null;

  function loadConfetti() {
    return new Promise(function (resolve, reject) {
      if (confettiLib) { resolve(confettiLib); return; }
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js';
      s.onload = function () { confettiLib = window.confetti; resolve(confettiLib); };
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function fireConfetti(tier) {
    const canvas = document.getElementById('F3ConfettiCanvas');
    if (!canvas) return;
    loadConfetti().then(function (confetti) {
      const myConfetti = confetti.create(canvas, { resize: true, useWorker: false });
      // F3 red + white + light gray
      const opts = tier === 2
        ? { particleCount: 80, spread: 90, startVelocity: 30, colors: ['#bb0101', '#ffffff', '#eeeeee'] }
        : { particleCount: 60, spread: 70, startVelocity: 25, colors: ['#bb0101', '#ffffff', '#eeeeee'] };
      myConfetti(Object.assign({ origin: { y: 0.5 } }, opts));
    }).catch(function () { /* confetti is a nice-to-have */ });
  }

  function checkAndFireConfetti(contentsEl) {
    if (!contentsEl) return;
    const bars = contentsEl.querySelectorAll('[data-progress-tier]');
    bars.forEach(function (bar) {
      const tier = bar.dataset.progressTier;
      const pct = parseInt(bar.dataset.progressPct, 10);
      const key = 'f3_confetti_tier' + tier + '_shown';
      if (pct >= 100 && !sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        fireConfetti(parseInt(tier, 10));
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* ESC key                                                               */
  /* ------------------------------------------------------------------ */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawer();
  });

  /* ------------------------------------------------------------------ */
  /* Public API                                                            */
  /* ------------------------------------------------------------------ */
  window.F3CartDrawer = {
    open: openDrawer,
    close: closeDrawer,
    refresh: refreshCart
  };

})();
